<template>
  <main-template>
    <v-container
      fluid
      class="pa-0 fill-height chat-container"
      :class="{ 'conversa-selecionada': !!conversaAtiva }"
    >
      <v-row class="ma-0 fill-height" style="height: calc(100vh - 120px)">
        <v-col
          cols="12"
          md="4"
          class="pa-0 fill-height border-right d-flex flex-column chat-sidebar"
        >
          <v-card
            flat
            tile
            class="pa-4 grey lighten-4 d-flex align-center justify-space-between border-bottom"
          >
            <span class="text-h6 font-weight-black grey--text text--darken-3">Mensagens</span>
            <v-btn
              v-if="podeIniciarConversa"
              color="#4f008f"
              dark
              rounded
              small
              elevation="1"
              @click="abrirDialogNovaConversa"
            >
              <v-icon left small>mdi-chat-plus-outline</v-icon>
              Nova Conversa
            </v-btn>
          </v-card>

          <div
            v-if="carregandoConversas"
            class="d-flex flex-column align-center justify-center flex-grow-1 py-12"
          >
            <v-progress-circular indeterminate color="var(--brand)" size="36"></v-progress-circular>
            <span class="caption grey--text mt-3">Carregando conversas...</span>
          </div>

          <div
            v-else-if="conversas.length === 0"
            class="d-flex flex-column align-center justify-center flex-grow-1 pa-6 text-center"
          >
            <v-avatar color="var(--surface-hover)" size="72" class="mb-4">
              <v-icon color="var(--brand)" size="36">mdi-chat-outline</v-icon>
            </v-avatar>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">
              Nenhuma conversa iniciada
            </div>
            <div class="text-caption grey--text mt-1" v-if="podeIniciarConversa">
              Clique em "Nova Conversa" para iniciar um diálogo com um colaborador.
            </div>
            <div class="text-caption grey--text mt-1" v-else>
              Aguarde que um administrador inicie uma conversa com você.
            </div>
          </div>

          <!-- Conversas List -->
          <v-list v-else class="flex-grow-1 overflow-y-auto pa-0" two-line>
            <template v-for="conversa in conversas">
              <v-list-item
                :key="conversa.id"
                @click="selecionarConversa(conversa)"
                class="conversa-item px-4 border-bottom"
                :class="{ 'conversa-ativa': conversaAtiva && conversaAtiva.id == conversa.id }"
              >
                <v-list-item-avatar class="elevation-1">
                  <v-img
                    v-if="obterUrlFoto(obterOutroParticipante(conversa))"
                    :src="obterUrlFoto(obterOutroParticipante(conversa))"
                  ></v-img>
                  <span v-else class="font-weight-black purple--text text--darken-3">
                    {{ obterIniciais(obterNomeDestinatario(conversa)) }}
                  </span>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title
                    class="font-weight-bold text-subtitle-2 grey--text text--darken-3"
                  >
                    {{ obterNomeDestinatario(conversa) }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    class="text-caption text-truncate mt-1 pr-6"
                    :class="{
                      'font-weight-bold grey--text text--darken-4': conversa.nao_lidas_count > 0,
                    }"
                  >
                    {{
                      conversa.ultima_mensagem ? conversa.ultima_mensagem.conteudo : "Sem mensagens"
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="align-self-center my-0">
                  <span class="text-caption grey--text mb-1">
                    {{
                      formatarHora(
                        conversa.ultima_mensagem
                          ? conversa.ultima_mensagem.created_at
                          : conversa.updated_at
                      )
                    }}
                  </span>
                  <v-badge
                    color="red"
                    :content="conversa.nao_lidas_count"
                    :value="conversa.nao_lidas_count > 0"
                    inline
                    class="ma-0"
                  ></v-badge>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-col>

        <v-col
          cols="12"
          md="8"
          class="pa-0 fill-height d-flex flex-column grey lighten-5 chat-area"
        >
          <template v-if="conversaAtiva">
            <v-card flat tile class="pa-3 grey lighten-4 d-flex align-center border-bottom">
              <v-btn icon class="mr-2 btn-voltar-mobile" @click="voltarParaLista">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>

              <v-avatar size="40" class="mr-3 elevation-1">
                <v-img
                  v-if="obterUrlFoto(obterOutroParticipante(conversaAtiva))"
                  :src="obterUrlFoto(obterOutroParticipante(conversaAtiva))"
                ></v-img>
                <span v-else class="font-weight-black purple--text text--darken-3">
                  {{ obterIniciais(obterNomeDestinatario(conversaAtiva)) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-bold text-subtitle-1 grey--text text--darken-3">
                  {{ obterNomeDestinatario(conversaAtiva) }}
                </div>
                <div class="caption success--text d-flex align-center font-weight-medium">
                  <v-icon x-small color="success" class="mr-1">mdi-circle</v-icon>
                  Ativo no chat
                </div>
              </div>
            </v-card>

            <div
              ref="historicoMensagens"
              class="flex-grow-1 overflow-y-auto pa-6 d-flex flex-column message-history"
            >
              <div
                v-if="carregandoMensagens"
                class="d-flex align-center justify-center flex-grow-1"
              >
                <v-progress-circular
                  indeterminate
                  color="var(--brand)"
                  size="32"
                ></v-progress-circular>
              </div>
              <template v-else>
                <div
                  v-for="msg in conversaAtiva.mensagens"
                  :key="msg.id"
                  class="d-flex mb-4"
                  :class="{ 'justify-end': msg.user_id == user.id }"
                >
                  <v-avatar
                    size="32"
                    class="mr-2 align-self-end elevation-1"
                    v-if="msg.user_id != user.id"
                  >
                    <v-img
                      v-if="obterUrlFoto(msg.remetente)"
                      :src="obterUrlFoto(msg.remetente)"
                    ></v-img>
                    <span v-else class="caption font-weight-black purple--text text--darken-3">
                      {{ obterIniciais(msg.remetente ? msg.remetente.name : "") }}
                    </span>
                  </v-avatar>

                  <div
                    class="message-bubble pa-3"
                    :class="msg.user_id == user.id ? 'message-sent' : 'message-received'"
                  >
                    <div
                      class="text-caption font-weight-bold mb-1 opacity-75"
                      v-if="msg.user_id != user.id"
                    >
                      {{ msg.remetente ? msg.remetente.name : "Outro" }}
                    </div>
                    <div class="text-body-2 text-pre-wrap">{{ msg.conteudo }}</div>
                    <div class="text-right mt-1" style="font-size: 0.7rem; opacity: 0.7">
                      {{ formatarDataHora(msg.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <v-card flat tile class="pa-3 grey lighten-4 border-top">
              <v-row class="ma-0 align-center">
                <v-textarea
                  v-model="novaMensagem"
                  placeholder="Digite sua mensagem..."
                  outlined
                  rounded
                  dense
                  hide-details
                  rows="1"
                  auto-grow
                  class="message-input mr-2"
                  @keydown.enter.exact.prevent="enviarMensagem"
                ></v-textarea>
                <v-btn
                  fab
                  dark
                  color="#4f008f"
                  small
                  elevation="1"
                  :disabled="!novaMensagem.trim() || enviandoMensagem"
                  @click="enviarMensagem"
                >
                  <v-progress-circular
                    v-if="enviandoMensagem"
                    indeterminate
                    size="20"
                    width="2"
                    color="white"
                  ></v-progress-circular>
                  <v-icon v-else>mdi-send</v-icon>
                </v-btn>
              </v-row>
            </v-card>
          </template>

          <div
            v-else
            class="d-flex flex-column align-center justify-center flex-grow-1 pa-12 text-center"
          >
            <v-avatar color="var(--brand-soft-2)" size="96" class="mb-4">
              <v-icon color="var(--brand)" size="48">mdi-chat-processing-outline</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-black grey--text text--darken-3">Suas Conversas</div>
            <div class="text-body-2 grey--text mt-2" style="max-width: 400px">
              Selecione uma conversa ao lado para visualizar o histórico de mensagens ou clique em
              "Nova Conversa" para iniciar um novo diálogo.
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="dialogNovaConversa" max-width="500px" scrollable>
      <v-card rounded="xl" class="pa-4">
        <v-card-title class="px-2 pt-2 pb-4 d-flex justify-space-between align-center">
          <span class="text-h6 font-weight-black grey--text text--darken-3">Nova Conversa</span>
          <v-btn icon small @click="dialogNovaConversa = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="px-2 py-4" style="height: 350px">
          <div
            v-if="carregandoColaboradores"
            class="d-flex flex-column align-center justify-center py-12"
          >
            <v-progress-circular indeterminate color="var(--brand)" size="32"></v-progress-circular>
            <span class="caption grey--text mt-3">Carregando colaboradores...</span>
          </div>

          <div
            v-else-if="colaboradores.length === 0"
            class="d-flex flex-column align-center justify-center py-12 text-center"
          >
            <v-icon size="48" color="grey lighten-1">mdi-account-off-outline</v-icon>
            <div class="text-subtitle-2 font-weight-bold grey--text mt-2">
              Nenhum colaborador ativo encontrado
            </div>
            <div class="text-caption grey--text mt-1">
              Todos os colaboradores ativos da sua franquia já possuem conversa ou não há
              colaboradores cadastrados.
            </div>
          </div>

          <div v-else>
            <div class="text-caption font-weight-bold mb-2 grey--text">Selecione o colaborador</div>
            <v-list dense class="pa-0 border rounded-lg overflow-y-auto" style="max-height: 180px">
              <v-list-item-group v-model="colaboradorSelecionadoId" color="primary">
                <v-list-item
                  v-for="colab in colaboradores"
                  :key="colab.id"
                  :value="colab.id"
                  class="border-bottom"
                >
                  <v-list-item-avatar class="elevation-1">
                    <v-img v-if="obterUrlFoto(colab)" :src="obterUrlFoto(colab)"></v-img>
                    <span v-else class="font-weight-black purple--text text--darken-3 caption">
                      {{ obterIniciais(colab.name) }}
                    </span>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold text-body-2">{{
                      colab.name
                    }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{
                      colab.email
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>

            <v-textarea
              v-model="mensagemInicial"
              label="Escreva sua primeira mensagem..."
              outlined
              rounded
              class="mt-4"
              rows="3"
              hide-details
              :disabled="!colaboradorSelecionadoId"
            ></v-textarea>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-2 pt-4 pb-2 d-flex justify-end">
          <v-btn text rounded large class="font-weight-bold" @click="dialogNovaConversa = false"
            >Cancelar</v-btn
          >
          <v-btn
            color="#4f008f"
            dark
            rounded
            large
            class="font-weight-bold px-6"
            :disabled="!colaboradorSelecionadoId || !mensagemInicial.trim() || enviandoNovaConversa"
            @click="enviarNovaConversa"
          >
            <v-progress-circular
              v-if="enviandoNovaConversa"
              indeterminate
              size="20"
              width="2"
              color="white"
              class="mr-2"
            ></v-progress-circular>
            Iniciar Conversa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ChatConversasPage",
  data() {
    return {
      conversas: [],
      conversaAtiva: null,
      conversaAtivaIndex: null,
      carregandoConversas: false,
      carregandoMensagens: false,
      novaMensagem: "",
      enviandoMensagem: false,
      intervalId: null,

      dialogNovaConversa: false,
      colaboradores: [],
      carregandoColaboradores: false,
      colaboradorSelecionadoId: null,
      mensagemInicial: "",
      enviandoNovaConversa: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeIniciarConversa() {
      if (!this.user) return false;
      const userRoles = (this.user.roles || []).map((r) => r.name);
      if (this.user.roleName) userRoles.push(this.user.roleName);
      return ["super-admin", "admin", "TI", "lider"].some((r) => userRoles.includes(r));
    },
  },
  async created() {
    await this.carregarConversas();
    this.iniciarPoller();

    const conversaId = this.$route.query.conversaId;
    if (conversaId) {
      const id = parseInt(conversaId, 10);
      const conversa = this.conversas.find((c) => c.id === id);
      if (conversa) {
        this.selecionarConversa(conversa);
      } else {
        this.carregarConversaPorId(id);
      }
    }
  },
  destroyed() {
    this.pararPoller();
  },
  methods: {
    async carregarConversas() {
      try {
        if (this.conversas.length === 0) {
          this.carregandoConversas = true;
        }
        const data = await this.$services.chatService.listarConversas();
        this.conversas = data || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoConversas = false;
      }
    },
    async carregarConversaPorId(id) {
      try {
        this.carregandoMensagens = true;
        const data = await this.$services.chatService.exibirConversa(id);
        this.conversaAtiva = data;
        this.conversaAtivaIndex = id;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoMensagens = false;
      }
    },
    async selecionarConversa(conversa) {
      this.conversaAtiva = conversa;
      this.conversaAtivaIndex = conversa.id;
      this.carregandoMensagens = true;
      try {
        const data = await this.$services.chatService.exibirConversa(conversa.id);
        this.conversaAtiva = data;
        const idx = this.conversas.findIndex((c) => c.id === conversa.id);
        if (idx !== -1) {
          this.conversas[idx].nao_lidas_count = 0;
        }
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoMensagens = false;
      }
    },
    async enviarMensagem() {
      if (!this.novaMensagem.trim() || !this.conversaAtiva || this.enviandoMensagem) return;
      this.enviandoMensagem = true;
      const texto = this.novaMensagem;
      this.novaMensagem = "";
      try {
        const data = await this.$services.chatService.enviarMensagem(this.conversaAtiva.id, texto);
        this.conversaAtiva = data;
        const idx = this.conversas.findIndex((c) => c.id === this.conversaAtiva.id);
        if (idx !== -1) {
          this.conversas[idx].ultima_mensagem =
            data.ultima_mensagem || data.mensagens[data.mensagens.length - 1];
          this.conversas[idx].updated_at = new Date().toISOString();
        }
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        this.novaMensagem = texto;
        this.$handleError(error);
      } finally {
        this.enviandoMensagem = false;
      }
    },
    async abrirDialogNovaConversa() {
      this.dialogNovaConversa = true;
      this.colaboradorSelecionadoId = null;
      this.mensagemInicial = "";
      this.carregandoColaboradores = true;
      try {
        const data = await this.$services.chatService.listarColaboradores();
        const activeUserIds = new Set(
          this.conversas
            .map((c) => {
              const outro = this.obterOutroParticipante(c);
              return outro ? Number(outro.id) : null;
            })
            .filter(Boolean)
        );
        this.colaboradores = (data || []).filter((colab) => !activeUserIds.has(Number(colab.id)));
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoColaboradores = false;
      }
    },
    async enviarNovaConversa() {
      if (
        !this.colaboradorSelecionadoId ||
        !this.mensagemInicial.trim() ||
        this.enviandoNovaConversa
      )
        return;
      this.enviandoNovaConversa = true;
      try {
        const data = await this.$services.chatService.iniciarConversa(
          this.colaboradorSelecionadoId,
          this.mensagemInicial
        );
        this.dialogNovaConversa = false;
        await this.carregarConversas();
        const conversa = this.conversas.find((c) => c.id === data.id);
        if (conversa) {
          this.selecionarConversa(conversa);
        } else {
          this.conversaAtiva = data;
          this.conversaAtivaIndex = data.id;
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.enviandoNovaConversa = false;
      }
    },
    obterOutroParticipante(conversa) {
      if (!conversa || !conversa.participantes || !this.user) return null;
      return conversa.participantes.find((p) => Number(p.id) !== Number(this.user.id)) || null;
    },
    obterNomeDestinatario(conversa) {
      const outro = this.obterOutroParticipante(conversa);
      return outro ? outro.name : "Desconhecido";
    },
    obterUrlFoto(participante) {
      if (!participante) return null;
      const rh = participante.recursos_humanos || participante.recursosHumanos;
      if (rh && rh.foto) {
        const apiHost = (process.env.VUE_APP_API_HOST || "").replace(/\/$/, "");
        const token = localStorage.getItem("access_token") || "";
        const tokenParam = token ? `?token=${token}` : "";
        return `${apiHost}/recursos-humanos/foto/${rh.foto}${tokenParam}`;
      }
      return null;
    },
    obterIniciais(nome) {
      if (!nome) return "U";
      return nome
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    formatarHora(dataStr) {
      if (!dataStr) return "";
      const d = new Date(dataStr);
      return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },
    formatarDataHora(dataStr) {
      if (!dataStr) return "";
      const d = new Date(dataStr);
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    scrollToBottom() {
      const container = this.$refs.historicoMensagens;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    voltarParaLista() {
      this.conversaAtiva = null;
      this.conversaAtivaIndex = null;
    },
    iniciarPoller() {
      this.pararPoller();
      this.intervalId = setInterval(async () => {
        const prevActiveId = this.conversaAtiva ? this.conversaAtiva.id : null;
        try {
          const listData = await this.$services.chatService.listarConversas();
          this.conversas = listData || [];

          if (prevActiveId) {
            const activeData = await this.$services.chatService.exibirConversa(prevActiveId);
            if (
              !this.conversaAtiva ||
              activeData.mensagens.length !== this.conversaAtiva.mensagens.length
            ) {
              const currentScrollIsAtBottom = this.checkIfScrollIsAtBottom();
              this.conversaAtiva = activeData;
              if (currentScrollIsAtBottom) {
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
              }
            }
          }
        } catch (e) {
          console.error("Erro no polling do chat:", e);
        }
      }, 5000);
    },
    pararPoller() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    checkIfScrollIsAtBottom() {
      const container = this.$refs.historicoMensagens;
      if (!container) return false;
      return container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    },
  },
};
</script>

<style scoped>
.chat-container {
  overflow: hidden;
  background-color: var(--surface-2);
}

.border-right {
  border-right: 1px solid var(--border) !important;
}

.border-bottom {
  border-bottom: 1px solid var(--border) !important;
}

.border-top {
  border-top: 1px solid var(--border) !important;
}

.conversa-item {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.conversa-item:hover {
  background-color: var(--surface-3) !important;
}

.conversa-ativa {
  background-color: var(--surface-hover) !important;
  border-left: 4px solid var(--brand);
}

.message-history {
  background-color: var(--surface-2);
  scroll-behavior: smooth;
}

.message-bubble {
  max-width: 70%;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

.message-sent {
  background: linear-gradient(135deg, #4f008f 0%, #6d28d9 100%);
  color: white;
  border-top-right-radius: 4px;
}

.message-received {
  background-color: var(--surface);
  color: var(--text-2);
  border-top-left-radius: 4px;
  border: 1px solid var(--border);
}

/* Iniciais dos avatares (purple--text text--darken-3) legíveis no escuro. */
.theme--dark .purple--text.text--darken-3 {
  color: var(--brand) !important;
  caret-color: var(--brand) !important;
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.opacity-75 {
  opacity: 0.75;
}

.message-input >>> .v-input__control >>> .v-input__slot {
  background-color: var(--surface) !important;
  border-radius: 28px !important;
}

@media (max-width: 959px) {
  .chat-sidebar {
    display: flex !important;
  }
  .chat-area {
    display: none !important;
  }
  .btn-voltar-mobile {
    display: inline-flex !important;
  }

  .conversa-selecionada .chat-sidebar {
    display: none !important;
  }
  .conversa-selecionada .chat-area {
    display: flex !important;
  }
}

@media (min-width: 960px) {
  .btn-voltar-mobile {
    display: none !important;
  }
}
</style>
